import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Paper,
  Divider
} from '@mui/material';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' }
];

/**
 * WorkHoursEditor Component
 * Allows editing of weekly work hours for Monday-Friday
 * Each day can have: Start Time, End Time, or N/A
 */
const WorkHoursEditor = ({ workHours, onChange }) => {
  // Initialize workHours if not provided
  const currentWorkHours = workHours || {};

  const handleDayTypeChange = (day, type) => {
    const newWorkHours = { ...currentWorkHours };
    
    if (type === 'na') {
      // Set to N/A - remove times
      newWorkHours[day] = { type: 'na' };
    } else {
      // Set to working day with default times if not already set
      newWorkHours[day] = {
        type: 'working',
        startTime: newWorkHours[day]?.startTime || '09:00',
        endTime: newWorkHours[day]?.endTime || '17:00'
      };
    }
    
    onChange(newWorkHours);
  };

  const handleTimeChange = (day, field, value) => {
    const newWorkHours = { ...currentWorkHours };
    
    if (!newWorkHours[day]) {
      newWorkHours[day] = { type: 'working', startTime: '09:00', endTime: '17:00' };
    }
    
    newWorkHours[day] = {
      ...newWorkHours[day],
      [field]: value
    };
    
    onChange(newWorkHours);
  };

  const getDayType = (day) => {
    return currentWorkHours[day]?.type || 'na';
  };

  const getStartTime = (day) => {
    return currentWorkHours[day]?.startTime || '09:00';
  };

  const getEndTime = (day) => {
    return currentWorkHours[day]?.endTime || '17:00';
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Weekly Work Hours
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set the working hours for each day of the week (Monday - Friday)
      </Typography>
      
      <Paper variant="outlined" sx={{ p: 2 }}>
        {DAYS_OF_WEEK.map((day, index) => (
          <Box key={day.key}>
            {index > 0 && <Divider sx={{ my: 2 }} />}
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {day.label}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={9}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={getDayType(day.key)}
                    onChange={(e) => handleDayTypeChange(day.key, e.target.value)}
                  >
                    <FormControlLabel 
                      value="working" 
                      control={<Radio size="small" />} 
                      label="Working Day" 
                    />
                    <FormControlLabel 
                      value="na" 
                      control={<Radio size="small" />} 
                      label="N/A" 
                    />
                  </RadioGroup>
                </FormControl>
                
                {getDayType(day.key) === 'working' && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      size="small"
                      value={getStartTime(day.key)}
                      onChange={(e) => handleTimeChange(day.key, 'startTime', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 900, // 15 min intervals
                      }}
                      sx={{ width: 150 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      to
                    </Typography>
                    <TextField
                      label="End Time"
                      type="time"
                      size="small"
                      value={getEndTime(day.key)}
                      onChange={(e) => handleTimeChange(day.key, 'endTime', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 900, // 15 min intervals
                      }}
                      sx={{ width: 150 }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default WorkHoursEditor;

